import { ChevronRight } from "lucide-react";
import { useState } from "react";

import {
  DEAL_JUMP_GROUPS,
  DEAL_TOP_ID,
  DEAL_TRAILING_JUMPS,
} from "@/lib/deal-capture/paths";
import { scrollToSection } from "@/lib/deal-capture/scroll";
import { cn } from "@/lib/utils";
import type { DealCaptureDealOption } from "@/types/deal-capture";

import { ExploreRow } from "./ExploreRow";

/**
 * The open deal's sections in the Explore tree: its card, then a jump link per
 * section of the deal page, under the page's two collapsible groups. Every
 * row scrolls the page to its section and filters nothing.
 */
export function DealJumpTree({
  deal,
  onClose,
}: {
  deal: DealCaptureDealOption;
  onClose: () => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});

  return (
    <div className="flex flex-col gap-0.5">
      <ExploreRow label="Overview" onJump={() => scrollToSection(DEAL_TOP_ID)} onClose={onClose} />
      <div className="my-1 rounded-md border-l-2 border-l-primary bg-surface-elevated px-2.5 py-2">
        <div className="text-xs font-bold uppercase tracking-eyebrow text-text-dim">Deal</div>
        <div className="text-sm font-semibold text-primary">#{deal.number}</div>
        <div className="truncate text-xs text-muted-foreground" title={deal.description}>
          {deal.description}
        </div>
      </div>
      {DEAL_JUMP_GROUPS.map((group) => {
        const expanded = open[group.id] ?? true;
        return (
          <div key={group.id} className="flex flex-col gap-0.5">
            <div className="flex items-center">
              <div className="min-w-0 flex-1">
                <ExploreRow label={group.label} onJump={() => scrollToSection(group.id)} onClose={onClose} />
              </div>
              <button
                type="button"
                aria-expanded={expanded}
                aria-label={`${expanded ? "Collapse" : "Expand"} ${group.label}`}
                onClick={() => setOpen((s) => ({ ...s, [group.id]: !expanded }))}
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-row-hover hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronRight
                  size={14}
                  className={cn("transition-transform", expanded ? "rotate-90" : "rotate-0")}
                />
              </button>
            </div>
            {expanded &&
              group.items.map((item) => (
                <ExploreRow
                  key={item.id}
                  label={item.label}
                  indent
                  onJump={() => scrollToSection(item.id)}
                  onClose={onClose}
                />
              ))}
          </div>
        );
      })}
      {DEAL_TRAILING_JUMPS.map((item) => (
        <ExploreRow
          key={item.id}
          label={item.label}
          onJump={() => scrollToSection(item.id)}
          onClose={onClose}
        />
      ))}
    </div>
  );
}
